import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import { toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../../i18n";
import router from "../../../../../router";
import store from "../../../../../store";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { has, have } from "../../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../../utils/page";
import { flatWhereGroup } from "../../../../../utils/search";
import { required, requiredWhen, validateError } from "../../../../../utils/validate";
import websocket from "../../../../../utils/websocket";
import { callPersonType } from "../../../../account/register/register.section";
import { callUserObjectiveMe } from "../../role-mgt/role-mgt.logic";
import { callCompanyRoleMgt } from "../../role-mgt/role-mgt.section";
import { mapLinkageGetData } from "../tab-1/tab1.logic";
import { loadRequestCheck } from "../tab-3/tab3.logic";
import { callUserRequestDoc, callUserRequestType } from "../tab-4/tab4.section";
import { callUserRequestDoc5Attachment, onLinkIdCard } from "../tab-5/tab5.section";
import { createForm as createFormUserAuthorize } from "./tab6-data.logic";
import { createConditionAction, createConditionForm } from "./tab6.condition";
import { createColumns, createField } from "./tab6.section";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/userRequest";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ props, page, state, companyId }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const whereCompanyNameIndex = where.findIndex(d => d.name === "refUserAuthorizeTemp.companyName");
    if (whereCompanyNameIndex !== -1) {
        customWhere.splice(
            whereCompanyNameIndex,
            1,
            ...[
                { ...where[whereCompanyNameIndex], openBucket: true },
                { ...where[whereCompanyNameIndex], closeBucket: true, logic: "OR", name: "refUserAuthorizeTemp.fullName" }
            ]
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props, companyId })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly, routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ props, id, companyId, forLog }) => {
    if (!forLog) {
        return {
            select: ["*", "refUserAuthorize.*", "refUserAuthorize.refPersonType.*", "refUserAuthorizeTemp.**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props, companyId })],
            children: [
                { name: "refUserAuthorizeTemp.roles", select: ["*", "refRole.*"], page: 0, size: 1000000 },
                { name: "refUserAuthorizeTemp.attachments", select: ["*"], page: 0, size: 1000000, sort: [{ name: "createDate", type: "ASC" }] },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "in", value: ["draft", "reEdit"] }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forOfficeRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceiveCustoms", "waitApproveCenter", "waitDocCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        refDepartment: null,
        statusRemark: null,
        refUserAuthorize: null,
        refUserAuthorizeTemp: createFormUserAuthorize(),
        statuses: [],
        lastStatus: {
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forOfficeRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refRequestType`]: [required],
        [`${prefix}refUserAuthorize`]: [requiredWhen({ when: () => model.refRequestType?.key === "2" })],
        [`${prefix}refUserAuthorizeTemp.refPersonType`]: [required],
        [`${prefix}refUserAuthorizeTemp.companyName`]: [requiredWhen({ when: () => model.refUserAuthorizeTemp.refPersonType?.key === "1" })],
        [`${prefix}refUserAuthorizeTemp.refDocumentTypePerson`]: [
            requiredWhen({ when: () => model.refUserAuthorizeTemp.refPersonType?.key === "2" })
        ],
        [`${prefix}refUserAuthorizeTemp.idCard`]: [required],
        [`${prefix}refUserAuthorizeTemp.refTitle`]: [requiredWhen({ when: () => model.refUserAuthorizeTemp.refPersonType?.key === "2" })],
        [`${prefix}refUserAuthorizeTemp.firstName`]: [requiredWhen({ when: () => model.refUserAuthorizeTemp.refPersonType?.key === "2" })],
        [`${prefix}refUserAuthorizeTemp.lastName`]: [requiredWhen({ when: () => model.refUserAuthorizeTemp.refPersonType?.key === "2" })],
        [`${prefix}refUserAuthorizeTemp.roles`]: [required],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && model.refRequestType?.key === "2" && (!model.id || ["draft", "reEdit"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesAttachment = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refUserAuthorizeTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refUserAuthorizeTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refUserAuthorizeTemp.attachments.${index}.size`] = [
                requiredWhen({ when: () => model.refUserAuthorizeTemp.attachments[index].attribute?.isRequiredAttachment })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    if (!modelRef.id) {
        return i18n.global.t("button.create");
    }
    return modelRef.refUserAuthorizeTemp.idCard;
};

export const createWhereFor = ({ props, companyId }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refOwner.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (have({ menuContext, keys: ["forCustoms", "forCenter"] })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    return [
        { name: "refOwner.id", type: "UUID", operator: "=", value: companyId },
        { name: "refRequestDoc.key", type: "String", operator: "=", value: "6" },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const loadForm = ({ props, data, refs, state, companyId }) => {
    setIsLoading(true);
    const { form, routerPrefix } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, state, companyId, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page, routerPrefix }))
        .then(() => loadDefaultForm({ props, data, refs, routerPrefix }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] }, routerPrefix }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callUserRequestType({}),
        callUserRequestDoc({}),
        callPersonType({}),
        callUserRequestDoc5Attachment({}),
        callUserObjectiveMe({}),
        callCompanyRoleMgt({})
    ];
    return Promise.all(promises).then(arr => {
        const userInfo = store.state.resource.auth.info?.user;
        data.userRequestTypes = arr[0];
        data.userRequestDocs = arr[1];
        data.personTypes = arr[2];
        data.userRequestDoc5Attachment = arr[3];
        data.userObjectives = arr[4];
        data.companyRoleMgts = arr[5];
        if ((!id && !form) || forceDefault) {
            modelRef.refOwner = userInfo;
            modelRef.refRequestType = data.userRequestTypes.find(d => d.key === "1");
            modelRef.refRequestDoc = data.userRequestDocs.find(d => d.key === "6");
            modelRef.refUserAuthorizeTemp.refPersonType = data.personTypes.find(d => d.key === "1");
            data.userRequestDoc5Attachment.forEach(item =>
                modelRef.refUserAuthorizeTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
        }
    });
};

export const initState = ({ props, companyId }) => {
    const { menuContext, routerPrefix } = props;
    if (stateGroup[companyId]) {
        return stateGroup[companyId];
    }
    const state = {
        columns: createColumns(),
        page: createPage({
            options: {
                select: [
                    "*",
                    "refRequestType.*",
                    "refStatus.*",
                    "refUserAuthorizeTemp.*",
                    "refUserAuthorizeTemp.refPersonType.*",
                    "refUserAuthorizeTemp.refDocumentTypePerson.*"
                ],
                sort: [{ name: "updateDate", type: "DESC" }],
                children: [
                    {
                        name: "statuses",
                        asName: "lastStatus.approve",
                        select: ["**"],
                        page: 0,
                        size: 1,
                        where: [
                            {
                                name: "refStatus.key",
                                type: "String",
                                operator: "=",
                                value: "approve"
                            }
                        ],
                        sort: [{ name: "createDate", type: "DESC" }]
                    }
                ]
            }
        }),
        tableOptions: { search: { activeKey: "Search" } },
        conditionAction: createConditionAction(),
        tabKey: "1",
        websocketId: null,
        websocketSubscribes: []
    };

    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.refUserAuthorizeTemp.fullName).join(", ") }),
            onClick: ({ rowSelection }) =>
                onClickActiveAll({
                    rowSelection,
                    callActiveAll,
                    state,
                    onSearch: search => onSearch({ props, state, companyId, ...search }),
                    setIsLoading
                })
        }),
        createManageArchive({
            menuContext,
            confirm: ({ rowSelection }) => ({
                content: rowSelection.selectedRows.map(d => d.refUserAuthorizeTemp.fullName).join(", "),
                okType: "danger"
            }),
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({
                    rowSelection,
                    callArchiveAll,
                    state,
                    onSearch: search => onSearch({ props, state, companyId, ...search }),
                    setIsLoading
                })
        }),
        createManageDelete({
            menuContext,
            confirm: ({ rowSelection }) => ({
                content: rowSelection.selectedRows.map(d => d.refUserAuthorizeTemp.fullName).join(", "),
                okType: "danger"
            }),
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({
                    rowSelection,
                    callDeleteAll,
                    state,
                    onSearch: search => onSearch({ props, state, companyId, ...search }),
                    setIsLoading
                }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
    ];
    return (stateGroup[companyId] = state);
};

export const initForm = ({ props, data, refs, state, companyId }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs, state, companyId });
    data.form = createForm({ props, data, refs, state, companyId });
    data.rules = createRules({ props, data, refs, state, companyId });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, state, companyId, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, state, companyId, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refUserAuthorizeTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesAttachment({ props, data, refs }),
        itemsKey: "refUserAuthorizeTemp.attachments"
    });
    loadRequestCheck({ props, data, refs, state, companyId }).then(page => {
        if (page.data.length > 0) {
            router.push({ name: routerPrefix });
            notification["error"]({ message: i18n.global.t("views.company.mgt.cannotCreateRequest") });
            return Promise.resolve();
        }
        loadForm({ props, data, refs, state, companyId }).then(() => {
            if (props.setAppPageHeader) {
                setAppPageHeader({
                    slots: {
                        top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                        default: () => createAppPageHeaderDefault({ props, data, refs, state, companyId })
                    }
                });
            }
        });
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs, state }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    let statusRemark = null;
                    if ([undefined, "draft", "reEdit"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceiveCustoms", "waitApproveCenter", "waitDocCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forOfficeRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        formParams: { statusRemark }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const onWebsocketConnect = ({ props, data, state }) => {
    state.websocketSubscribes.push(
        websocket.subscribe(`/user/topic/linkage/get/data`, res => {
            mapLinkageGetData({ data, key: "refUserAuthorizeTemp", linkageRes: res });
            if (data.linkageReceive001 && data.linkageReceive009) {
                onLinkIdCard({ props, data, isLoading })(data.linkIdCard);
            }
            setIsLoadingApp(false);
        })
    );
};

export const stateGroup = [];
